import { isEqual } from "lodash";
import { AitCellData } from "./AitCellData";
import { AitHeaderCellData } from "./interface";

/**
 * Replace text within a cell's content and return an updated cell copy.
 *
 * The function applies `replaceText` to either the cell's `replacedText`
 * (when present) or its `text` and sets `replacedText` on the returned cell
 * when the replacement changed the value; otherwise `replacedText` is left
 * undefined to indicate no change.
 *
 * @template T - Cell payload type
 * @template R - Specific cell type (body or header)
 * @param cell - Cell object to copy and update
 * @param oldText - The substring to replace (plain text)
 * @param newText - Replacement payload value
 * @param replaceText - Callback that given current payload, oldPhrase and newPhrase returns updated payload
 * @returns A shallow copy of `cell` with `replacedText` set when a change occurred
 */
export const replaceCellText = <T, R extends AitCellData<T> | AitHeaderCellData<T>>(
  cell: R,
  oldText: string,
  newText: T,
  replaceText: (s: T, oldPhrase: string, newPhrase: T) => T,
): R => {
  const replacedText = replaceText(cell.replacedText ?? cell.text, oldText, newText);
  return {
    ...cell,
    replacedText: !isEqual(cell.text, replacedText) ? replacedText : undefined,
  };
};
